import { CircularProgress, Switch } from '@civilgrid/ui/components';
import { useRef, useState } from 'react';
import { getOrganization } from '~/api/internal';
import { setTestEnvOverride } from '~/api/organizations2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail } from '~/common/types';
import Notify from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';
import { MajorHeading } from '../common/Heading';

export default function TestEnvOverride() {
  const [pickedOrganization, setPickedOrganization] = useState<OrganizationDetail | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationDetail | null>(null);
  const selectionCountRef = useRef(0);
  const selectedOrganizationId = pickedOrganization?.id ?? '';
  const [useTestEnv, setUseTestEnv] = useState<'UAT' | null>(null);

  const handleSetTestEnvOverride = async (isPressed: boolean) => {
    const previousTestEnv = useTestEnv;
    const nextTestEnv = isPressed ? 'UAT' : null;
    const selection = selectionCountRef.current;
    setUseTestEnv(nextTestEnv);
    try {
      await setTestEnvOverride(selectedOrganizationId, nextTestEnv);
      capturePosthogEvent(POSTHOG_EVENTS.organizationTestEnvOverrideToggled, {
        organization_id: selectedOrganizationId,
        enabled: isPressed,
      });
    } catch (_err) {
      // Skip the rollback if another pick has replaced the toggle's state since the save started.
      if (selectionCountRef.current === selection) setUseTestEnv(previousTestEnv);
      Notify.error({ message: 'Error updating test environment override' });
    }
  };

  const handleOrganizationSelect = async (organizationId: string, organization: OrganizationDetail) => {
    const selection = ++selectionCountRef.current;
    setPickedOrganization(organization);
    setSelectedOrganization(null);
    try {
      const organizationDetail = await getOrganization(organizationId);
      if (selectionCountRef.current !== selection) return;
      setSelectedOrganization(organizationDetail);
      setUseTestEnv(organizationDetail.useTestEnv ?? null);
    } catch (_err) {
      if (selectionCountRef.current !== selection) return;
      setPickedOrganization(null);
      Notify.error({ message: 'Error loading organization' });
    }
  };

  return (
    <div className="col-span-2">
      <div className="space-y-2">
        <MajorHeading>Select Organization to Use Test Env</MajorHeading>
        <div className="flex items-center space-x-2">
          <span className="font-semibold">Organization:</span>
          <OrganizationPicker
            selectedOrganization={pickedOrganization}
            onOrganizationSelect={handleOrganizationSelect}
          />
        </div>
      </div>
      {pickedOrganization && !selectedOrganization && <CircularProgress indeterminate />}
      {selectedOrganization && (
        <>
          <div className="pt-4 text-sm">
            Use the test environment instead of production for{' '}
            <span className="font-semibold">{selectedOrganization.name}</span>.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className={`text-sm ${useTestEnv ? 'text-grey-50' : 'text-grey-90 font-semibold'}`}>Off</span>
              <Switch
                data-testid="test-env-override-toggle"
                aria-label="Use test environment"
                checked={!!useTestEnv}
                onCheckedChange={handleSetTestEnvOverride}
              />
              <span className={`text-sm ${useTestEnv ? 'text-grey-90 font-semibold' : 'text-grey-50'}`}>On</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
